"use client";

import { useCallback, useEffect, useState } from "react";

import Image from "next/image";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import Autoplay from "embla-carousel-autoplay";
// import ScrollingBanner from "@/components/global/ui/ScrollingBanner";

import { PageShopSchema } from "@/sanity/pages/PageShopTemplate";

import cornerArrow from "@/public/icons/corner-arrow.svg";

export default function Hero({ data }: { data: PageShopSchema }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const changeCurrent = useCallback(
    (index: number) => {
      if (!api) {
        return;
      }

      api.scrollTo(index);
    },
    [api],
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <section id="shop" className="w-full bg-[#F3F2F1] overflow-clip">
      <div className="w-full">
        {data?.heroItems && (
          <div className="w-full flex flex-col items-center justify-center">
            <Carousel
              className="w-full [&_>_div]:!overflow-visible"
              setApi={setApi}
              opts={{
                align: "start",
                skipSnaps: false,
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 5000,
                  stopOnInteraction: false,
                  stopOnMouseEnter: true,
                }),
              ]}
            >
              <CarouselContent className="w-full gap-x-[2rem]">
                {data?.heroItems?.map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="p-0 w-full flex flex-col items-start relative"
                  >
                    {item?.image && (
                      <Image
                        className="hidden lg:block h-[115rem] w-full object-cover object-right"
                        src={item.image}
                        alt="Produktbild"
                      />
                    )}

                    {item?.mobileImage && (
                      <Image
                        className="lg:hidden h-[91rem] md:h-[120rem] w-full object-cover object-top"
                        src={item.mobileImage}
                        alt="Produktbild"
                      />
                    )}

                    {/* fade */}
                    <div className="hidden lg:block absolute inset-0 z-[1] w-full h-full bg-gradient-to-r from-black to-transparent"></div>
                    {/* end fade */}

                    <div className="absolute z-[2] top-[auto] bottom-[4rem] lg:bottom-[auto] lg:top-1/2 left-0 lg:-translate-y-1/2 w-[min(77rem,100%)] flex flex-col items-start px-[2rem] lg:ps-[7rem] lg:px-0">
                      <div className="flex flex-col gap-[1rem] select-none">
                        {item?.texts && (
                          <PortableText
                            value={item.texts ?? []}
                            components={customRichTextComponent({
                              block: {
                                h6: ({ children }) => (
                                  <Chip
                                    className="[&_img]:!w-[2rem]"
                                    image={cornerArrow}
                                    text={children as string}
                                  />
                                ),
                                h1: ({ children }) => (
                                  <h1 className="font-funnel text-[4.5rem] lg:text-[9rem] font-[500] !text-white leading-[100%]">
                                    {children}
                                  </h1>
                                ),
                                normal: ({ children }) => (
                                  <p className="text-white/60 text-[2rem] lg:text-[2.25rem]">
                                    {children}
                                  </p>
                                ),
                              },
                            })}
                          />
                        )}
                      </div>

                      <div className="w-full mb-[4rem] lg:mb-[12rem] lg:mt-[1rem]">
                        {item?.cta && (
                          <CTAButton
                            data={item?.cta}
                            className="[&_.button]:!py-[1.5rem] [&_.button]:lg:!py-[2rem]"
                          />
                        )}
                      </div>

                      <div className="flex gap-[1rem] ps-2">
                        {data?.heroItems?.map((item2, index2) => (
                          <button
                            key={index2 + 100}
                            onClick={() => changeCurrent(index2)}
                            className={cn(
                              "min-w-[1.5rem] min-h-[1.5rem] w-[1.5rem] h-[1.5rem] rounded-full flex items-center justify-center relative",
                              index2 === current
                                ? "bg-white/100 after:absolute after:rounded-full after:border after:border-white after:w-[2.25rem] after:h-[2.25rem] after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2"
                                : "bg-white/30",
                            )}
                            aria-label={`Folie ${index2 + 1}`}
                          ></button>
                        ))}
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        )}
      </div>
      {/* {data?.heroFooterText && <ScrollingBanner text={data.heroFooterText} className="text-[#D9F9B9] bg-[#1E3739] !py-[1.5rem]" />} */}
    </section>
  );
}
